import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { 
  Wrench, 
  Sparkles, 
  Truck, 
  MoreHorizontal,
  UserPlus,
  Search,
  MessageSquare,
  Star,
  MapPin,
  Shield,
  Globe
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const categories = [
    { key: 'plumbing', icon: Wrench, color: 'bg-blue-500' },
    { key: 'cleaning', icon: Sparkles, color: 'bg-emerald-500' },
    { key: 'delivery', icon: Truck, color: 'bg-amber-500' },
    { key: 'others', icon: MoreHorizontal, color: 'bg-purple-500' }
  ];

  const steps = [
    { key: 'step1', icon: UserPlus },
    { key: 'step2', icon: Search },
    { key: 'step3', icon: MessageSquare }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">W</span>
            </div>
            <span className="font-bold text-xl text-slate-900">WorkMatch</span>
          </div>
          
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            
            {user ? (
              <Button 
                data-testid="go-to-dashboard-btn"
                onClick={() => navigate('/dashboard')}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-6"
              >
                {t('nav.home')}
              </Button>
            ) : (
              <>
                <Button 
                  data-testid="login-btn"
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  className="text-slate-700 hover:text-slate-900"
                >
                  {t('nav.login')}
                </Button>
                <Button 
                  data-testid="register-btn"
                  onClick={() => navigate('/register')}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-6"
                >
                  {t('nav.register')}
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight">
              {t('landing.hero_title')}
            </h1>
            <p className="text-lg text-slate-600 max-w-lg">
              {t('landing.hero_subtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                data-testid="get-started-btn"
                onClick={() => navigate(user ? '/dashboard' : '/register')}
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95"
              >
                {t('landing.get_started')}
              </Button>
              <Button 
                data-testid="find-jobs-btn"
                variant="outline"
                onClick={() => navigate(user ? '/dashboard' : '/login')}
                className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg px-8 py-6 text-lg font-medium"
              >
                {t('landing.find_jobs')}
              </Button>
            </div>
            
            {/* Trust indicators */}
            <div className="flex items-center gap-6 pt-4">
              <div className="flex items-center gap-2 text-slate-600">
                <Shield className="w-5 h-5 text-emerald-500" />
                <span className="text-sm">{t('landing.trusted_by')}</span>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.pexels.com/photos/8486978/pexels-photo-8486978.jpeg"
                alt="Professional worker"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Floating card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 flex items-center gap-3 animate-fade-in">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <Star className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">4.9/5</p>
                <p className="text-sm text-slate-500">10k+ reviews</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
            {t('landing.categories')}
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {categories.map((cat) => (
              <button
                key={cat.key}
                data-testid={`category-${cat.key}-btn`}
                onClick={() => navigate(user ? '/dashboard' : '/register')}
                className="group p-6 bg-white rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-200 text-left"
              >
                <div className={`w-14 h-14 ${cat.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <cat.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-semibold text-slate-900">{t(`landing.${cat.key}`)}</h3>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
            {t('landing.how_it_works')}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {steps.map((step, index) => (
              <div 
                key={step.key}
                className="relative p-8 bg-white rounded-2xl border border-slate-100 hover:border-slate-200 transition-colors"
              >
                <div className="absolute -top-4 -left-4 w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center text-white font-bold">
                  {index + 1}
                </div>
                <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center mb-6">
                  <step.icon className="w-8 h-8 text-slate-700" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {t(`landing.${step.key}_title`)}
                </h3>
                <p className="text-slate-600">
                  {t(`landing.${step.key}_desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Workers */}
      <section className="py-20 px-6 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1759301248268-b4ada0a5f964?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxkZWxpdmVyeSUyMGRyaXZlciUyMHdpdGglMjBwYWNrYWdlJTIwc2Nvb3RlcnxlbnwwfHx8fDE3NzAwODY4MjB8MA&ixlib=rb-4.1.0&q=85"
                alt="Delivery driver"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img 
                src="https://images.pexels.com/photos/6196239/pexels-photo-6196239.jpeg"
                alt="Cleaner"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1646227655718-dd721b681d91?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzAwODY4Mjl8MA&ixlib=rb-4.1.0&q=85"
                alt="Construction worker"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            {t('landing.hero_title')}
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            {t('landing.hero_subtitle')}
          </p>
          <Button 
            data-testid="cta-get-started-btn"
            onClick={() => navigate(user ? '/dashboard' : '/register')}
            className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
          >
            {t('landing.get_started')}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-200 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">W</span>
            </div>
            <span className="font-semibold text-slate-900">WorkMatch</span>
          </div>
          
          <div className="flex items-center gap-6 text-sm text-slate-600">
            <span>© 2024 WorkMatch</span>
            <span>•</span>
            <span>Nicolapps</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
